import { useState, useEffect, useMemo } from 'react';
import i18n from '../i18n/i18n';
import { SwipeableList, Type, SwipeableListItem, SwipeAction, TrailingActions } from 'react-swipeable-list';
import 'react-swipeable-list/dist/styles.css';

// Hooks
import { useTheme } from '../hooks/useTheme';

// Components imports
import HeaderBar from '../components/HeaderBar';
import SearchFilterBar from '../components/Search&FilterBar';
import SessionsSkeleton from '../components/Skeleton/SessionsSkeleton';
import Session from '../components/Session';
import DrawerCreateSession from '../components/Drawer/DrawerCreateSession';

// MUI imports
import { Box, Typography, useMediaQuery } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenIcon from '@mui/icons-material/LockOpen';

// Helpers
import { getSessions } from '../helpers/api/callSession';
import { handleScroll } from '../helpers/functions/handleScroll';
import { sortAscendingSession } from '../helpers/functions/sortSession';
import { useUserStore } from '../store/UserStore';
import { closeSession, openSession } from '../helpers/api/callSession';
import { normalizeStr } from '../helpers/functions/normalizeStr';

// Types
import { SessionData } from '../types/sessionData';
import { ScrollPosition } from '../types/enumScrollPosition';
import { SessionFilterType, FilterType } from '../types/enumSessionFilter';

const Sessions = () => {
    const [currentFilter, setCurrentFilter] = useState<FilterType>(SessionFilterType.CURRENT_WEEK);
    const [scrollPosition, setScrollPosition] = useState<ScrollPosition>(ScrollPosition.TOP);
    const [sessionList, setSessionList] = useState<Array<SessionData>>([]);
    const [isLoading, setIsLoading] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');

    const theme = useTheme();
    const { user } = useUserStore();
    const smallScreen = useMediaQuery('(max-height:700px)');

    useEffect(() => {
        const fetchSessions = async () => {
            try {
                setIsLoading(true);
                const sessions = await getSessions(user.id as string, currentFilter);
                const sortedSessions = sortAscendingSession(sessions);
                setSessionList(sortedSessions);
            } catch (error) {
                console.error('Error fetching sessions:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSessions();
    }, [user.id, currentFilter]);

    const filteredSessions = useMemo(() => {
        if (!searchTerm) return sessionList;
        const q = normalizeStr(searchTerm);
        return sessionList.filter((s) => normalizeStr(s.trainingLabel).includes(q));
    }, [sessionList, searchTerm]);

    const handleSwipeClose = (sessionId: string) => {
        try {
            closeSession(sessionId);
            setSessionList((prev) => prev.map((session) => (session.id === sessionId ? { ...session, closingDate: new Date().toISOString() } : session)));
        } catch (error) {
            console.error('Error closing session:', error);
        }
    };

    const handleSwipeUnClose = (sessionId: string) => {
        try {
            openSession(sessionId);
            setSessionList((prev) => prev.map((session) => (session.id === sessionId ? { ...session, closingDate: null } : session)));
        } catch (error) {
            console.error('Error closing session:', error);
        }
    };

    const trailingActions = (sessionId: string, closingDate: string | null) => (
        <TrailingActions>
            <SwipeAction destructive={false} onClick={() => (closingDate ? handleSwipeUnClose(sessionId) : handleSwipeClose(sessionId))}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette.closed.main,
                        color: theme.palette.text.secondary,
                        height: '100%',
                        padding: '0 0.5em',
                        fontSize: '0.95em',
                        fontWeight: '500'
                    }}
                >
                    {closingDate ? (
                        <>
                            <LockOpenIcon sx={{ fontSize: '1.8em' }} />
                            {i18n.t('Sessions.actions.reopen')}
                        </>
                    ) : (
                        <>
                            <LockOutlinedIcon sx={{ fontSize: '1.8em' }} />
                            {i18n.t('Sessions.actions.close')}
                        </>
                    )}
                </Box>
            </SwipeAction>
        </TrailingActions>
    );

    if (isLoading) return <SessionsSkeleton />;

    if (sessionList.length === 0)
        return (
            <Box sx={{ height: '100vh', backgroundColor: theme.palette.background.default, color: theme.palette.text.primary }}>
                <HeaderBar />
                <SearchFilterBar currentFilter={currentFilter} onFilterChange={setCurrentFilter} searchTerm={searchTerm} onSearchChange={setSearchTerm} />
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', margin: '0 auto', width: '75%', height: '70vh', marginTop: '1em' }}>
                    <Typography sx={{ textAlign: 'center', fontSize: '1em', color: theme.palette.text.primary }}>{i18n.t('Sessions.noSessions')}</Typography>
                    <DrawerCreateSession buttonStatus="fixed" scrollPosition="top" />
                </Box>
            </Box>
        );

    return (
        <Box sx={{ height: '100vh', backgroundColor: theme.palette.background.default, color: theme.palette.text.primary }}>
            <HeaderBar />
            <SearchFilterBar currentFilter={currentFilter} onFilterChange={setCurrentFilter} searchTerm={searchTerm} onSearchChange={setSearchTerm} />
            <Box
                className="list-container"
                onScroll={(event) => handleScroll(event, setScrollPosition)}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    marginTop: '1em',
                    maxHeight: smallScreen ? 'calc(100vh - 6vh - 6vh - 1em)' : 'calc(100vh - 6vh - 6vh - 0.3em)',
                    overflowY: 'auto'
                }}
            >
                <SwipeableList fullSwipe={true} type={Type.IOS}>
                    {filteredSessions.map((session, index) => (
                        <SwipeableListItem trailingActions={trailingActions(session.id, session.closingDate ?? null)} key={session.id}>
                            <Session session={session} nbSessions={filteredSessions.length} customStyle={index < filteredSessions.length - 1} />
                        </SwipeableListItem>
                    ))}
                </SwipeableList>

                <Typography variant="subtitle1" sx={{ margin: '1.5em 0 1.5em 0.75em', textAlign: 'center', zIndex: 1, color: theme.palette.secondary.main }}>
                    {filteredSessions.length > 1
                        ? `${filteredSessions.length} ${i18n.t('Sessions.sessions', { count: filteredSessions.length })}`
                        : filteredSessions.length === 1
                          ? '1 ' + i18n.t('Sessions.session')
                          : i18n.t('Sessions.noSessions')}
                </Typography>
            </Box>
            <DrawerCreateSession buttonStatus="floating" scrollPosition={scrollPosition} />
        </Box>
    );
};

export default Sessions;
